import React, { useState, useEffect } from 'react';
import { useNavigate, Link, Navigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import { LogIn, UserPlus, Brain, Sparkles } from 'lucide-react';
import DocumentTitle from '@/components/DocumentTitle';

const Login = () => {
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { login, register, user } = useAuth();
  
  // Get tab from URL query parameter
  const queryParams = new URLSearchParams(location.search);
  const tabParam = queryParams.get('tab');
  const [activeTab, setActiveTab] = useState(tabParam === 'register' ? 'register' : 'login');

  // Update tab state when URL query parameter changes
  useEffect(() => {
    if (tabParam === 'register') {
      setActiveTab('register');
    } else if (tabParam === 'login') {
      setActiveTab('login');
    }
  }, [tabParam]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    const result = await login(loginEmail, loginPassword);
    
    setIsLoading(false);
    if (result.success) {
      navigate('/dashboard');
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (registerPassword !== confirmPassword) {
      setPasswordError("Passwords don't match");
      return;
    }
    
    if (registerPassword.length < 6) {
      setPasswordError("Password must be at least 6 characters");
      return;
    }
    
    setPasswordError('');
    setIsLoading(true);
    const result = await register(registerEmail, registerPassword);
    setIsLoading(false);
    
    if (result.success) {
      navigate('/dashboard');
    }
  };

  // Handle tab change and update URL
  const handleTabChange = (value: string) => {
    setActiveTab(value);
    navigate(`/login${value === 'register' ? '?tab=register' : ''}`);
  };

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-slate-900 to-purple-900 relative overflow-hidden">
      <DocumentTitle 
        title="Sign In - VideoBrain.ai | Access Your Video Intelligence Dashboard"
        description="Sign in to your VideoBrain.ai account to access your video intelligence dashboard, manage your videos, and unlock the hidden value in your content."
      />
      
      {/* Back to home link */}
      <div className="absolute top-4 left-4 z-50">
        <Button variant="ghost" asChild className="text-white hover:bg-white/10">
          <Link to="/" className="flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
              <path d="m12 19-7-7 7-7"></path>
              <path d="M19 12H5"></path>
            </svg>
            Back to Home
          </Link>
        </Button>
      </div>
      
      {/* Background stars/grid effect */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxkZWZzPjxwYXR0ZXJuIGlkPSJncmlkIiB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHBhdHRlcm5Vbml0cz0idXNlclNwYWNlT25Vc2UiPjxwYXRoIGQ9Ik0gNDAgMCBMIDAgMCAwIDQwIiBmaWxsPSJub25lIiBzdHJva2U9InJnYmEoMjU1LDI1NSwyNTUsMC4wNSkiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3QgeD0iMCIgeT0iMCIgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIgLz48L3N2Zz4=')]"></div>
      
      {/* Star decoration elements */}
      <div className="absolute top-1/4 left-1/5 animate-pulse">
        <Sparkles className="text-pink-500/30 h-8 w-8" />
      </div>
      <div className="absolute bottom-1/3 right-1/4 animate-pulse delay-300">
        <Sparkles className="text-indigo-500/30 h-6 w-6" />
      </div>
      
      {/* Space particles animation */}
      <div className="stars absolute inset-0">
        {[...Array(50)].map((_, i) => (
          <div 
            key={i} 
            className="absolute rounded-full bg-white" 
            style={{
              width: `${Math.random() * 2 + 1}px`,
              height: `${Math.random() * 2 + 1}px`,
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              opacity: Math.random() * 0.7 + 0.3,
              animation: `twinkle ${Math.random() * 5 + 3}s infinite ${Math.random() * 5}s`
            }}
          />
        ))}
      </div>
      
      {/* Floating planets */}
      <div className="absolute -right-20 top-1/3 w-40 h-40 rounded-full bg-gradient-to-br from-purple-500/30 to-indigo-500/20 blur-xl animate-float"></div>
      <div className="absolute -left-16 top-2/3 w-32 h-32 rounded-full bg-gradient-to-br from-pink-500/30 to-red-500/20 blur-lg animate-float-delay"></div>
      
      <div className="flex-1 flex flex-col items-center justify-center px-4 py-12">
        {/* Logo and branding */}
        <div className="mb-8 text-center">
          <div className="flex justify-center mb-4">
            <div className="relative">
              <div className="h-20 w-20 rounded-full bg-pink-500/80 flex items-center justify-center blur-[20px] absolute -inset-1"></div>
              <div className="h-20 w-20 rounded-full bg-pink-600 flex items-center justify-center relative">
                <Brain className="h-10 w-10 text-white" />
              </div>
            </div>
          </div>
          
          <h1 className="text-5xl md:text-6xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-pink-500 to-purple-400 mb-2">
            VideoBrain
          </h1>
          
          <p className="text-lg text-gray-300 max-w-md mx-auto">
            <span className="text-pink-400">A revolutionary AI platform</span> that will transform how we understand, process, and interact with video content <span className="text-pink-400">forever</span>.
          </p>
        </div>
        
        {/* Auth container */}
        <div className="w-full max-w-md relative">
          <div className="absolute -inset-1 bg-gradient-to-r from-pink-500 to-purple-500 rounded-xl blur-md"></div>
          <div className="relative bg-black/40 backdrop-blur-md border border-white/10 p-8 rounded-lg shadow-xl">
            <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-8 bg-white/10">
                <TabsTrigger value="login" className="data-[state=active]:bg-pink-600">Login</TabsTrigger>
                <TabsTrigger value="register" className="data-[state=active]:bg-pink-600">Register</TabsTrigger>
              </TabsList>
              
              <TabsContent value="login">
                <div className="space-y-6">
                  <div>
                    <h2 className="text-2xl font-bold text-center text-white">Sign in to your account</h2>
                    <p className="mt-2 text-sm text-gray-300 text-center">
                      Access your videos and AI-powered insights
                    </p>
                  </div>
                  
                  <form onSubmit={handleLogin} className="space-y-6">
                    <div className="space-y-2">
                      <label htmlFor="email" className="text-sm font-medium text-gray-200">
                        Email address
                      </label>
                      <Input
                        id="email"
                        type="email"
                        value={loginEmail}
                        onChange={(e) => setLoginEmail(e.target.value)}
                        placeholder="name@company.com"
                        required
                        className="w-full bg-white/10 border-white/20 text-white"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <label htmlFor="password" className="text-sm font-medium text-gray-200">
                          Password
                        </label>
                        <Link to="#" className="text-sm text-pink-400 hover:underline">
                          Forgot password?
                        </Link>
                      </div>
                      <Input
                        id="password"
                        type="password"
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        placeholder="••••••••"
                        required
                        className="w-full bg-white/10 border-white/20 text-white"
                      />
                    </div>
                    
                    <Button type="submit" className="w-full bg-pink-600 hover:bg-pink-700" disabled={isLoading}>
                      {isLoading ? (
                        <span className="flex items-center gap-2">
                          <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></span>
                          Signing in...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <LogIn className="h-4 w-4" />
                          Sign in
                        </span>
                      )}
                    </Button>
                    
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t border-white/10" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-black/40 px-2 text-gray-400">
                          Or continue with
                        </span>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <Button variant="outline" type="button" className="bg-pink-600 w-full border-white/20 text-white hover:bg-white/10">
                        Google
                      </Button>
                      <Button variant="outline" type="button" className="bg-pink-600 w-full border-white/20 text-white hover:bg-white/10">
                        GitHub
                      </Button>
                    </div>
                  </form>
                </div>
              </TabsContent>
              
              <TabsContent value="register">
                <div className="space-y-6">
                  <div>
                    <h2 className="text-2xl font-bold text-center text-white">Create your account</h2>
                    <p className="mt-2 text-sm text-gray-300 text-center">
                      Start unlocking the value in your videos
                    </p>
                  </div>
                  
                  <form onSubmit={handleRegister} className="space-y-6">
                    <div className="space-y-2">
                      <label htmlFor="register-email" className="text-sm font-medium text-gray-200">
                        Email address
                      </label>
                      <Input
                        id="register-email"
                        type="email"
                        value={registerEmail}
                        onChange={(e) => setRegisterEmail(e.target.value)}
                        placeholder="name@company.com"
                        required
                        className="w-full bg-white/10 border-white/20 text-white"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <label htmlFor="register-password" className="text-sm font-medium text-gray-200">
                        Password
                      </label>
                      <Input
                        id="register-password"
                        type="password"
                        value={registerPassword}
                        onChange={(e) => {
                          setRegisterPassword(e.target.value);
                          setPasswordError('');
                        }}
                        placeholder="••••••••"
                        required
                        className="w-full bg-white/10 border-white/20 text-white"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <label htmlFor="confirm-password" className="text-sm font-medium text-gray-200">
                        Confirm Password
                      </label>
                      <Input
                        id="confirm-password"
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => {
                          setConfirmPassword(e.target.value);
                          setPasswordError('');
                        }}
                        placeholder="••••••••"
                        required
                        className="w-full bg-white/10 border-white/20 text-white"
                      />
                      {passwordError && (
                        <p className="text-sm text-red-400 mt-1">{passwordError}</p>
                      )}
                    </div>
                    
                    <Button type="submit" className="w-full bg-pink-600 hover:bg-pink-700" disabled={isLoading}>
                      {isLoading ? (
                        <span className="flex items-center gap-2">
                          <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></span>
                          Creating account...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <UserPlus className="h-4 w-4" />
                          Create account
                        </span>
                      )}
                    </Button>
                    
                    <div className="text-sm text-center text-gray-400">
                      By creating an account, you agree to our{" "}
                      <Link to="#" className="text-pink-400 hover:underline">
                        Terms of Service
                      </Link>{" "}
                      and{" "}
                      <Link to="#" className="text-pink-400 hover:underline">
                        Privacy Policy
                      </Link>
                    </div>
                  </form>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>
        
        <div className="mt-10 text-center">
          <p className="text-gray-400 text-sm">COMING SOON</p>
        </div>
      </div>
      
      {/* CSS for animations */}
      <style>
      {`
        @keyframes twinkle {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 1; }
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        
        .animate-float-delay {
          animation: float 8s ease-in-out infinite 1s;
        }
      `}
      </style>
    </div>
  );
};

export default Login;
